import { lazy, Suspense, useState, useEffect } from 'react'
import { Card } from '@chakra-ui/react'
import WorkforceTabs, { type WorkforceSubTab } from './WorkforceTabs'
import { useRole } from '../../hooks/auth/useRole'
import { useQuery } from '@tanstack/react-query'
import { myPermissionsQueryOptions } from '../../lib/performance/queryOptions'
import RouteContentFallback from '../common/RouteContentFallback'

const loadMeetingRooms = () => import('../../pages/user/Calender/MeetingRoomsPage')
const loadOffices = () => import('../../pages/user/Calender/OfficesPage')
const loadDesks = () => import('../../pages/user/Calender/DesksPage')

const MeetingRooms = lazy(loadMeetingRooms)
const Offices = lazy(loadOffices)
const Desks = lazy(loadDesks)

interface WorkspaceProps {
  initialSubTab?: WorkforceSubTab
}

export default function Workspace({ initialSubTab }: WorkspaceProps = {}) {
  const { isSuperAdmin, isOrgAdmin } = useRole()
  const hasFullAccess = isOrgAdmin || isSuperAdmin

  const { data: myPermissions } = useQuery(myPermissionsQueryOptions(!hasFullAccess))

  const allowedTabs = hasFullAccess
    ? ['rooms', 'desks', 'offices']
    : myPermissions?.planning || []

  const [activeSubTab, setActiveSubTab] = useState<WorkforceSubTab>(
    initialSubTab || 'meeting-rooms',
  )

  // If current sub-tab is no longer allowed, switch to first allowed
  useEffect(() => {
    const currentPermKey = activeSubTab === 'meeting-rooms' ? 'rooms' : activeSubTab
    if (allowedTabs.length > 0 && !allowedTabs.includes(currentPermKey)) {
      // Find the first allowed workspace sub-tab
      if (allowedTabs.includes('rooms')) setActiveSubTab('meeting-rooms')
      else if (allowedTabs.includes('desks')) setActiveSubTab('desks')
      else if (allowedTabs.includes('offices')) setActiveSubTab('offices')
    }
  }, [allowedTabs, activeSubTab])

  const handleTabIntent = (tab: WorkforceSubTab) => {
    if (tab === 'meeting-rooms') void loadMeetingRooms()
    if (tab === 'offices') void loadOffices()
    if (tab === 'desks') void loadDesks()
  }

  useEffect(() => {
    handleTabIntent(activeSubTab)
    if (allowedTabs.includes('rooms')) void loadMeetingRooms()
    if (allowedTabs.includes('offices')) void loadOffices()
    if (allowedTabs.includes('desks')) void loadDesks()
  }, [activeSubTab, allowedTabs])

  return (
    <Card width="100%" p="0">
      <WorkforceTabs activeTab={activeSubTab} onSelectTab={setActiveSubTab} allowedTabs={allowedTabs} onTabIntent={handleTabIntent} />

      <Suspense fallback={<RouteContentFallback />}>
        {activeSubTab === 'meeting-rooms' && <MeetingRooms />}
        {activeSubTab === 'offices' && <Offices />}
        {activeSubTab === 'desks' && <Desks />}
      </Suspense>
    </Card>
  )
}
